import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/** Number sense and whole-number arithmetic — the foundation everything else stands on. */
export const ARITHMETIC_LESSONS: Lesson[] = [
  lesson({
    id: 'counting-01',
    unitId: 'unit-number-sense',
    conceptId: 'counting',
    title: 'Counting and Quantity',
    summary: 'Numbers name how many. Each number is exactly one more than the one before it.',
    objectives: ['Count objects accurately', 'Compare two quantities', 'Understand that the last number counted is the total'],
    estimatedMinutes: 20,
    sections: {
      introduction: {
        title: 'What a number is',
        blocks: [
          text('A number answers the question "how many?". Five is not a shape or a colour — it is the thing that six sheep, six stones and six seconds all have in common, minus one.'),
          text('Counting is how we find out which number applies.'),
        ],
      },
      intuition: {
        title: 'One more, every time',
        blocks: [
          text('Counting works because the numbers come in a fixed order and each one is exactly one bigger than the last. That is the entire structure of the counting numbers.'),
          math('1 \\rightarrow 2 \\rightarrow 3 \\rightarrow 4 \\rightarrow 5'),
          insight('The last number you say when counting a group is the size of the whole group. That sounds obvious, but it is a real idea — and it is what makes counting useful rather than just a chant.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'number-line', from: 0, to: 10, step: 1 }, 'Every step along the line is one more.'),
          figure({ kind: 'array', rows: 2, columns: 5, label: '10 counters' }),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('There are 7 birds on a wire. Three more land. How many now?'),
          steps([
            { label: 'Start at the number you have.', latex: '7' },
            { label: 'Count on, once for each new bird.', latex: '8, \; 9, \; 10' },
            { label: 'The last number you said is the answer.', latex: '10' },
          ]),
          tip('Counting on from the larger number is faster than starting again from 1. That small habit becomes mental addition later.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('counting', 4)] },
      independent: { title: 'On your own', blocks: [practice('counting', 4)] },
      challenge: { title: 'Something harder', blocks: [practice('counting', 2)] },
      review: {
        title: 'What to remember',
        blocks: [list(['Each number is one more than the one before.', 'The last number counted is the total.', 'Counting on is quicker than counting all.'])],
      },
    },
  }),

  lesson({
    id: 'place-value-01',
    unitId: 'unit-number-sense',
    conceptId: 'place-value',
    title: 'Place Value',
    summary: 'The same digit means different amounts depending on where it sits. Move it one place left and it means ten times more.',
    objectives: ['Name the value of each digit in a number', 'Write numbers in expanded form', 'Read and write large numbers'],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Ten digits, infinitely many numbers',
        blocks: [
          text('We have only ten symbols: 0 through 9. Yet we can write any number at all, however large. That works because of one idea — a digit\'s value depends on its position.'),
          math('3 \\quad 30 \\quad 300 \\quad 3000', 'The same digit, four very different amounts.'),
        ],
      },
      intuition: {
        title: 'Each place is ten times the last',
        blocks: [
          text('Reading right to left: ones, tens, hundreds, thousands. Each place is worth ten of the place to its right.'),
          text('So in 4,382, the 3 is not worth three. It sits in the hundreds place, so it is worth three hundred.'),
          insight('This is why our system is called base ten: everything is built from repeated groups of ten.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'place-value', digits: '4382', places: ['Thousands', 'Hundreds', 'Tens', 'Ones'] }),
          table(['Digit', 'Place', 'Value'], [['4', 'Thousands', '4,000'], ['3', 'Hundreds', '300'], ['8', 'Tens', '80'], ['2', 'Ones', '2']]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Write 6,207 in expanded form and say what the 2 is worth.'),
          steps([
            { label: 'Name each place from the right.', latex: '7 \\text{ ones}, \; 0 \\text{ tens}, \; 2 \\text{ hundreds}, \; 6 \\text{ thousands}' },
            { label: 'Write each digit times its place value.', latex: '6000 + 200 + 0 + 7' },
            { label: 'The 2 sits in the hundreds place.', latex: '200' },
          ]),
          warning('The zero is doing real work here. Without it, 6,207 would collapse to 627 and mean something else entirely. Zero is a placeholder, not nothing.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('place-value', 4, 'What is the value of the highlighted digit?')] },
      independent: { title: 'On your own', blocks: [practice('place-value', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('place-value', 3)] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A digit\'s value depends on its place.',
            'Each place is ten times the place to its right.',
            'Zero holds a place open so the other digits stay where they belong.',
          ]),
          insight('Place value comes back for decimals, for column arithmetic, and for rounding. It is the single most reused idea in elementary mathematics.'),
        ],
      },
    },
  }),

  lesson({
    id: 'rounding-01',
    unitId: 'unit-number-sense',
    conceptId: 'rounding',
    title: 'Rounding',
    summary: 'Replacing a number with a nearby simpler one — deliberately, and by a rule everyone agrees on.',
    objectives: ['Round to the nearest ten, hundred and thousand', 'Apply the halfway rule', 'Use rounding to estimate an answer'],
    estimatedMinutes: 20,
    sections: {
      introduction: {
        title: 'Close enough, on purpose',
        blocks: [
          text('If a stadium holds 48,217 people, you would say "about fifty thousand". You have not made a mistake — you have chosen a number that is easier to think about and close enough to be honest.'),
          text('Rounding is that choice, made by a rule instead of by feel.'),
        ],
      },
      intuition: {
        title: 'Which one is it nearer?',
        blocks: [
          text('To round 67 to the nearest ten, ask: is it nearer 60 or 70? It is 7 past 60 and only 3 short of 70, so it rounds up to 70.'),
          text('Every rounding question is that same question. The rule about digits is just a shortcut for it.'),
          insight('Exactly halfway — like 65 — is a genuine tie. Mathematics needs one shared answer, so the convention is to round up.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'number-line', from: 60, to: 70, step: 1, marks: [{ at: 67, label: '67' }] }, '67 sits much closer to 70 than to 60.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Round 3,472 to the nearest hundred.'),
          steps([
            { label: 'Find the hundreds digit.', latex: '3{,}\\mathbf{4}72' },
            { label: 'Look at the digit immediately to its right.', latex: '7', note: 'That digit decides everything.' },
            { label: '7 is 5 or more, so round up.', latex: '3400 \\rightarrow 3500' },
            { label: 'Everything to the right becomes zero.', latex: '3{,}500' },
          ]),
          tip('Rounding is how you check your own work. If you calculate 3,472 + 2,891 and get 1,363, a quick estimate of 3,500 + 2,900 ≈ 6,400 tells you instantly that something went wrong.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('rounding', 4)] },
      independent: { title: 'On your own', blocks: [practice('rounding', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('rounding', 3)] },
      review: {
        title: 'What to remember',
        blocks: [list(['Look at the digit one place to the right.', '5 or more: round up. Less than 5: round down.', 'Digits to the right become zero.', 'Estimate first, calculate second — it catches big errors.'])],
      },
    },
  }),

  lesson({
    id: 'multi-digit-addition-01',
    unitId: 'unit-whole-number-operations',
    conceptId: 'multi-digit-addition',
    title: 'Multi-Digit Addition',
    summary: 'Add one column at a time, and carry a ten across when a column overflows.',
    objectives: ['Add multi-digit numbers in columns', 'Carry correctly when a column exceeds nine', 'Estimate to check a sum'],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Big numbers, small steps',
        blocks: [
          text('Nobody adds 4,657 and 2,896 in one go. You break it into four easy additions — ones, tens, hundreds, thousands — and handle each one separately.'),
          text('That is the whole method, and it works because of place value.'),
        ],
      },
      intuition: {
        title: 'Why carrying works',
        blocks: [
          text('When a column adds up to more than 9, it will not fit in one digit. But ten ones are exactly one ten, and ten tens are exactly one hundred — so the excess can move one place to the left, where it fits.'),
          insight('Carrying is not a trick. It is trading ten small units for one bigger unit, which is precisely what place value allows.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [figure({ kind: 'place-value', digits: '4657', places: ['Thousands', 'Hundreds', 'Tens', 'Ones'] }, 'Each column is added on its own.')],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Compute 476 + 358.'),
          steps([
            { label: 'Ones column.', latex: '6 + 8 = 14', note: 'Write 4, carry 1 ten.' },
            { label: 'Tens column, including the carry.', latex: '7 + 5 + 1 = 13', note: 'Write 3, carry 1 hundred.' },
            { label: 'Hundreds column, including the carry.', latex: '4 + 3 + 1 = 8' },
            { label: 'Read off the answer.', latex: '476 + 358 = 834' },
          ]),
          tip('Estimate first: 480 + 360 ≈ 840. Your answer of 834 is close, so it is probably right.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('multi-digit-addition', 4)] },
      independent: { title: 'On your own', blocks: [practice('multi-digit-addition', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('multi-digit-addition', 3)] },
      review: {
        title: 'What to remember',
        blocks: [list(['Line up the places, not the left edges.', 'Add right to left.', 'Carry when a column exceeds 9.', 'Estimate to check.'])],
      },
    },
  }),

  lesson({
    id: 'multi-digit-subtraction-01',
    unitId: 'unit-whole-number-operations',
    conceptId: 'multi-digit-subtraction',
    title: 'Multi-Digit Subtraction',
    summary: 'Subtract column by column, regrouping from the next place when a column runs short.',
    objectives: ['Subtract multi-digit numbers in columns', 'Regroup across one and several places', 'Check a difference by adding it back'],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'When the top digit is too small',
        blocks: [
          text('Subtraction goes column by column, just like addition. The complication is what to do when the digit on top is smaller than the one below it.'),
          math('\\begin{array}{r} 4\\,0\\,2 \\\\ -\;1\\,7\\,8 \\end{array}', 'You cannot take 8 from 2 — not yet.'),
        ],
      },
      intuition: {
        title: 'Borrowing is trading',
        blocks: [
          text('You are not really "borrowing" — nothing gets returned. You are trading one ten for ten ones, so the same total is written differently.'),
          math('402 = 4 \\text{ hundreds} + 0 \\text{ tens} + 2 \\text{ ones} = 3 \\text{ hundreds} + 9 \\text{ tens} + 12 \\text{ ones}'),
          insight('Both lines describe 402. Regrouping never changes the number; it only changes how the number is spelled, so that each column has enough to work with.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [figure({ kind: 'place-value', digits: '402', places: ['Hundreds', 'Tens', 'Ones'] }, 'Trading across a zero takes two steps: hundreds → tens, then tens → ones.')],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Compute 402 − 178.'),
          steps([
            { label: 'Ones: 2 is smaller than 8, so regroup. The tens column is 0, so go to the hundreds first.', latex: '402 \\rightarrow 3\\,\\text{hundreds},\; 10\\,\\text{tens},\; 2\\,\\text{ones}' },
            { label: 'Now trade one ten for ten ones.', latex: '3\\,\\text{hundreds},\; 9\\,\\text{tens},\; 12\\,\\text{ones}' },
            { label: 'Ones column.', latex: '12 - 8 = 4' },
            { label: 'Tens column.', latex: '9 - 7 = 2' },
            { label: 'Hundreds column.', latex: '3 - 1 = 2' },
            { label: 'Answer.', latex: '402 - 178 = 224' },
          ]),
          tip('Check by adding: 224 + 178 = 402. Addition undoes subtraction, so this catches every regrouping error.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('multi-digit-subtraction', 4)] },
      independent: { title: 'On your own', blocks: [practice('multi-digit-subtraction', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('multi-digit-subtraction', 3, 'These involve regrouping across zeros.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list(['Work right to left.', 'When the top digit is too small, trade one from the next place.', 'Trading across a zero takes two steps.', 'Check by adding the answer back.']),
          warning('The commonest error is taking the smaller digit from the larger regardless of which is on top. 2 − 8 is not 6. If the top is smaller, you must regroup.'),
        ],
      },
    },
  }),

  lesson({
    id: 'multiplication-facts-01',
    unitId: 'unit-whole-number-operations',
    conceptId: 'multiplication-facts',
    title: 'Multiplication',
    summary: 'Multiplication counts equal groups — and drawing it as an array shows why the order does not matter.',
    objectives: ['Interpret multiplication as equal groups and as an array', 'Explain why a × b = b × a', 'Recall products within 100'],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Addition, done efficiently',
        blocks: [
          text('Six plates, four biscuits on each. You could add 4 + 4 + 4 + 4 + 4 + 4. Or you could write 6 × 4 and be done.'),
          math('6 \\times 4 = 24'),
          text('Multiplication is what we use when the groups are all the same size — which, in the world, they very often are.'),
        ],
      },
      intuition: {
        title: 'Why order does not matter',
        blocks: [
          text('Six groups of four, or four groups of six? They sound different. Draw them as a rectangle of dots and you will see they are the same rectangle, turned.'),
          insight('a × b = b × a. This is worth knowing not just as a rule but as a fact about rectangles — and it halves the number of facts you have to learn.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'array', rows: 6, columns: 4, label: '6 × 4' }),
          figure({ kind: 'array', rows: 4, columns: 6, label: '4 × 6' }),
          text('Same 24 dots. The rectangle has simply been rotated.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A theatre has 7 rows with 8 seats in each. How many seats?'),
          steps([
            { label: 'Identify the groups and the size of each.', latex: '7 \\text{ rows} \\times 8 \\text{ seats}' },
            { label: 'Multiply.', latex: '7 \\times 8 = 56' },
            { label: 'Answer with the unit.', latex: '56 \\text{ seats}' },
          ]),
          tip('If you cannot recall 7 × 8, build it from one you know: 7 × 8 = 7 × 4 × 2 = 28 × 2 = 56. Reconstructing a fact is far better than guessing it.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('multiplication-facts', 5)] },
      independent: { title: 'On your own', blocks: [practice('multiplication-facts', 6)] },
      challenge: { title: 'Something harder', blocks: [practice('multiplication-facts', 3)] },
      review: {
        title: 'What to remember',
        blocks: [list(['Multiplication counts equal groups.', 'An array shows why order does not matter.', 'Multiplying by 0 gives 0; multiplying by 1 changes nothing.', 'Unknown facts can be rebuilt from known ones.'])],
      },
    },
  }),

  lesson({
    id: 'division-meaning-01',
    unitId: 'unit-whole-number-operations',
    conceptId: 'division-meaning',
    title: 'Division',
    summary: 'Division answers two different questions with the same calculation: how big is each share, and how many shares fit.',
    objectives: ['Interpret division as sharing and as grouping', 'Relate division to multiplication', 'Divide within 100'],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Two questions, one operation',
        blocks: [
          text('Twelve sweets, shared between 3 children. How many each? That is division.'),
          text('Twelve sweets, given out 3 at a time. How many children get some? That is also division — the same division.'),
          math('12 \\div 3 = 4'),
        ],
      },
      intuition: {
        title: 'Division undoes multiplication',
        blocks: [
          text('Asking "12 ÷ 3" is asking "3 times what gives 12?". So every division fact is a multiplication fact you already know, read backwards.'),
          math('3 \\times 4 = 12 \\quad \\Longleftrightarrow \\quad 12 \\div 3 = 4'),
          warning('Unlike multiplication, order matters completely. 12 ÷ 3 is 4, but 3 ÷ 12 is a quarter. Read the problem carefully to see which number is being shared out.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [figure({ kind: 'array', rows: 3, columns: 4, label: '12 shared into 3 rows' }, 'Twelve dots in three equal rows — four in each.')],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('45 pencils are packed into boxes of 9. How many boxes?'),
          steps([
            { label: 'This is a "how many groups" question.', latex: '45 \\div 9' },
            { label: 'Ask the multiplication question instead.', latex: '9 \\times ? = 45' },
            { label: 'Recall the fact.', latex: '9 \\times 5 = 45' },
            { label: 'Answer.', latex: '5 \\text{ boxes}' },
          ]),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('division-meaning', 4)] },
      independent: { title: 'On your own', blocks: [practice('division-meaning', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('division-meaning', 3)] },
      review: {
        title: 'What to remember',
        blocks: [list(['Division shares into equal groups, or counts how many groups fit.', 'Every division is a multiplication read backwards.', 'Order matters: a ÷ b is not b ÷ a.'])],
      },
    },
  }),

  lesson({
    id: 'division-remainders-01',
    unitId: 'unit-whole-number-operations',
    conceptId: 'division-remainders',
    title: 'Remainders',
    summary: 'When the sharing does not come out even, what is left over is part of the answer — and what to do with it depends on the question.',
    objectives: ['Divide with a remainder', 'Interpret a remainder in context', 'Check a division using multiplication and addition'],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'When it does not divide evenly',
        blocks: [
          text('Seventeen sweets shared between 5 children. Each child gets 3, and 2 sweets are left over. Those 2 are the **remainder**.'),
          math('17 \\div 5 = 3 \\text{ remainder } 2'),
        ],
      },
      intuition: {
        title: 'The remainder is always smaller than the divisor',
        blocks: [
          text('If 5 or more were left over, you could give everyone another one. So the remainder must be less than the number you are dividing by — always.'),
          insight('This gives you a free check on every answer. A remainder of 7 when dividing by 5 means you stopped one round too early.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [figure({ kind: 'grid', width: 5, height: 4, shaded: [[0, 0], [1, 0], [2, 0], [3, 0], [4, 0], [0, 1], [1, 1], [2, 1], [3, 1], [4, 1], [0, 2], [1, 2], [2, 2], [3, 2], [4, 2], [0, 3], [1, 3]] }, 'Three complete rows of five, and two left over.')],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A bus holds 40 people. 138 people need to travel. How many buses are needed?'),
          steps([
            { label: 'Divide.', latex: '138 \\div 40 = 3 \\text{ remainder } 18' },
            { label: 'Read the remainder in context.', latex: '18 \\text{ people still have no bus}', note: 'They cannot walk.' },
            { label: 'So round up, not down.', latex: '4 \\text{ buses}' },
          ]),
          warning('The arithmetic gives 3 remainder 18. The answer to the question is 4. Deciding what the remainder means is your job, not the calculation\'s.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('division-remainders', 4)] },
      independent: { title: 'On your own', blocks: [practice('division-remainders', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('division-remainders', 3, 'Word problems where the remainder changes the answer.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list(['The remainder is what is left after the equal shares.', 'It is always smaller than the divisor.', 'Check: quotient × divisor + remainder = the original number.', 'Context decides whether to round up, round down, or report the remainder.']),
        ],
      },
    },
  }),

  lesson({
    id: 'factors-multiples-01',
    unitId: 'unit-factors',
    conceptId: 'factors-multiples',
    title: 'Factors and Multiples',
    summary: 'Factors divide a number exactly; multiples are what it divides into. Two views of the same relationship.',
    objectives: ['List the factors of a number', 'List multiples of a number', 'Find the greatest common factor and least common multiple'],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Two directions from one fact',
        blocks: [
          text('3 × 4 = 12. From that single fact: 3 and 4 are **factors** of 12, and 12 is a **multiple** of both 3 and 4.'),
          text('Factors look downwards from a number; multiples look upwards. The relationship is the same one, seen from two ends.'),
        ],
      },
      intuition: {
        title: 'Factors come in pairs',
        blocks: [
          text('Every factor has a partner. For 12: 1 with 12, 2 with 6, 3 with 4. That pairing is why listing factors is quick — you find them two at a time and stop when they meet in the middle.'),
          insight('A number with exactly two factors — itself and 1 — is prime. A number with a factor pair where both are equal, like 36 = 6 × 6, is a perfect square.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('Every factor pair of 12 is a rectangle with 12 squares:'),
          figure({ kind: 'array', rows: 3, columns: 4, label: '3 × 4' }),
          figure({ kind: 'array', rows: 2, columns: 6, label: '2 × 6' }),
          table(['Number', 'Factors', 'First few multiples'], [['12', '1, 2, 3, 4, 6, 12', '12, 24, 36, 48'], ['18', '1, 2, 3, 6, 9, 18', '18, 36, 54, 72']]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the greatest common factor of 12 and 18, and their least common multiple.'),
          steps([
            { label: 'Factors of 12.', latex: '1, 2, 3, 4, 6, 12' },
            { label: 'Factors of 18.', latex: '1, 2, 3, 6, 9, 18' },
            { label: 'Largest shared factor.', latex: '\\gcd(12, 18) = 6' },
            { label: 'Multiples of 12: 12, 24, 36… Multiples of 18: 18, 36…', latex: '\\operatorname{lcm}(12, 18) = 36' },
          ]),
          tip('The GCF is what you divide by to simplify a fraction. The LCM is the least common denominator. This lesson is the machinery behind both.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('factors-multiples', 4)] },
      independent: { title: 'On your own', blocks: [practice('factors-multiples', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('factors-multiples', 3)] },
      review: {
        title: 'What to remember',
        blocks: [list(['Factors divide the number exactly; multiples are built from it.', 'Factors come in pairs.', 'GCF simplifies fractions; LCM gives common denominators.'])],
      },
    },
  }),

  lesson({
    id: 'order-of-operations-01',
    unitId: 'unit-factors',
    conceptId: 'order-of-operations',
    title: 'Order of Operations',
    summary: 'A shared convention, so that everyone who reads an expression gets the same answer.',
    objectives: ['Apply the conventional order of operations', 'Use brackets to override the order', 'Explain why the convention is needed'],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Two answers, one expression',
        blocks: [
          text('What is 2 + 3 × 4?'),
          text('Left to right gives 20. Multiplication first gives 14. Both are defensible readings — and that is exactly the problem. Mathematics cannot have an expression that means two things.'),
          math('2 + 3 \\times 4 = 14', 'The agreed answer.'),
        ],
      },
      intuition: {
        title: 'Why multiplication goes first',
        blocks: [
          text('This is a convention, not a discovery — but it is not arbitrary. Multiplication is repeated addition, so 3 × 4 is one quantity: twelve. Reading it first keeps groups together.'),
          text('The order is: brackets, then exponents, then multiplication and division (left to right), then addition and subtraction (left to right).'),
          warning('Multiplication and division sit at the *same* level, worked left to right — as do addition and subtraction. 8 ÷ 4 × 2 is 4, not 1.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          table(['Step', 'Operation', 'Example'], [
            ['1', 'Brackets', '(2 + 3) × 4 = 20'],
            ['2', 'Exponents', '2 + 3² = 11'],
            ['3', 'Multiply and divide, left to right', '8 ÷ 4 × 2 = 4'],
            ['4', 'Add and subtract, left to right', '10 − 3 + 2 = 9'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Evaluate 4 + 3 × (10 − 6) ÷ 2.'),
          steps([
            { label: 'Brackets first.', latex: '4 + 3 \\times 4 \\div 2' },
            { label: 'Multiplication and division, left to right. Multiply first because it is further left.', latex: '4 + 12 \\div 2' },
            { label: 'Then divide.', latex: '4 + 6' },
            { label: 'Finally add.', latex: '10' },
          ]),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('order-of-operations', 4)] },
      independent: { title: 'On your own', blocks: [practice('order-of-operations', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('order-of-operations', 3)] },
      review: {
        title: 'What to remember',
        blocks: [
          list(['Brackets, exponents, multiply/divide, add/subtract.', 'Multiplication and division share a level — go left to right.', 'Addition and subtraction share a level — go left to right.', 'When in doubt, add brackets to say what you mean.']),
          insight('This convention is what makes algebra readable. Every expression you meet from here on assumes it.'),
        ],
      },
    },
  }),
];
